import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, signOut, changePassword, deleteAccount } = useApp();
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  if (!userProfile) {
    return null;
  }

  const getMemberSince = () => {
    const now = new Date();
    const created = new Date(userProfile.createdAt);
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years === 1 ? '' : 's'} ago`;
    }
  };

  const handlePasswordChange = async () => {
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await changePassword(newPassword);
      setSuccess('Password changed successfully!');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await deleteAccount();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-secondary p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header with navigation */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate('/mmt/dashboard')}
            className="text-primary hover:underline"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => navigate('/mmt/landing')}
            className="bg-accent text-white px-6 py-2 rounded-full font-semibold hover:bg-accent/90 transition-all"
          >
            Connect Accounts
          </button>
        </div>

        <h1 className="text-5xl font-bold text-center mb-12">Profile</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        {/* Profile Information */}
        <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
          <h2 className="text-2xl font-bold mb-6 text-primary">Your Information</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-600">First Name</label>
                <p className="text-lg">{userProfile.firstName}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Last Name</label>
                <p className="text-lg">{userProfile.lastName}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-600">Email</label>
              <p className="text-lg">{userProfile.email}</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-600">Member Since</label>
              <p className="text-lg">{getMemberSince()}</p>
            </div>
          </div>
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
          <h2 className="text-2xl font-bold mb-6 text-primary">Account Settings</h2>

          {/* Change Password */}
          {!showPasswordChange ? (
            <button
              onClick={() => setShowPasswordChange(true)}
              className="w-full bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all mb-4"
            >
              Change Password
            </button>
          ) : (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="font-semibold mb-3">Change Password</h3>
              <div className="space-y-3">
                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  minLength={6}
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  minLength={6}
                />
                <div className="flex gap-3">
                  <button
                    onClick={handlePasswordChange}
                    disabled={loading}
                    className="flex-1 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50"
                  >
                    {loading ? 'Changing...' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordChange(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full bg-accent text-white px-6 py-3 rounded-xl font-semibold hover:bg-accent/90 transition-all mb-4"
          >
            Sign Out
          </button>
        </div>

        {/* Delete Account */}
        <div className="bg-red-50 rounded-3xl p-8 shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Danger Zone</h2>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-all"
            >
              Delete Account
            </button>
          ) : (
            <div className="space-y-4">
              <p className="text-red-700 font-semibold">
                Are you sure you want to delete your account? This action cannot be undone.
                All your data will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="flex-1 bg-red-600 text-white px-4 py-3 rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Yes, Delete My Account'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-3 rounded-xl font-semibold hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
